<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BtnLockResource',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'locked']);

  const props = defineProps({
    ...userComponentProps,
    resource: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    noIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    noText: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'sm',
    },
    tooltip: {
      type: String,
      required: false,
    },
    dialogHeader: {
      type: String,
      required: false,
    },
    dropdownItem: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    text: $fn.value.pgettext('BtnLockText', 'Lock'),
    tooltip: $fn.value.pgettext('BtnLockTooltip', 'Lock'),
  });
  const container = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    container: container.value,
    ux: ux.value,
  }));

  const $$load_container = () => {
    return {
      self: props.resource.links.parent,
      object: 'list',
      data: [],
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'container' }, { name: 'ux' }],
    loaders: { container: $$load_container },
    ux: {},
    dataRefs: { messages, container, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.resource,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resource');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );
  watch(
    () => props.noIcon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noIcon');
    }
  );
  watch(
    () => props.noText,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noText');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );
  watch(
    () => props.tooltip,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('tooltip');
    }
  );
  watch(
    () => props.dialogHeader,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dialogHeader');
    }
  );
  watch(
    () => props.dropdownItem,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dropdownItem');
    }
  );

  const act_7315092382398562_emit_28xp49vds_getActionArgs = async ($event) => {
    return {
      event: 'locked',
    };
  };
  const act_7315092382398562_rest_wahm726d0_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: props.resource,
      data: {
        etag: props.resource.etag,
        locked: true,
      },
    };
  };
  const act_7315092382398562_rest_wahm726d0_getConfirm = async ($event) => {
    return {
      header: props.dialogHeader,
      message: $fn.value.gettext(
        'Locking prevents further modifications. Please confirm this operation.'
      ),
      btnOk: $fn.value.gettext('Lock'),
      severity: 'warning',
    };
  };

  const act_7315092382398562_emit_28xp49vds = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_28xp49vds_getActionArgs,
  });
  const inst_evh_7315092382398562_success_wcasxe5p3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_28xp49vds,
      event: 'success',
      displayName: 'emit',
    });
  const act_7315092382398562_rest_wahm726d0 = new actionTypes.RestAction({
    actionArgs: act_7315092382398562_rest_wahm726d0_getActionArgs,
    confirm: act_7315092382398562_rest_wahm726d0_getConfirm,
    displayName: 'lock',
    events: [inst_evh_7315092382398562_success_wcasxe5p3],
  });
  const inst_evh_7315092382398562_clickPreventStop_6mrcwzxfp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_rest_wahm726d0,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_7315092382398562_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_clickPreventStop_6mrcwzxfp],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_success_wcasxe5p3,
    inst_evh_7315092382398562_clickPreventStop_6mrcwzxfp,
    lock: inst_evh_7315092382398562_clickPreventStop_6mrcwzxfp,
    inst_evh_7315092382398562_clickPreventStop,
  };

  const evh_7315092382398562_success_wcasxe5p3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_success_wcasxe5p3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop_6mrcwzxfp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop_6mrcwzxfp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :dropdownItem="dropdownItem"
    :text="noText ? null : messages.text"
    :icon="noIcon ? null : 'fa-lock'"
    :variant="dropdownItem ? 'default' : 'light'"
    :spinning="actions.lock.isRunning"
    :disabled="disabled"
    :size="size"
    :tooltip="tooltip || messages.tooltip"
    @click.prevent.stop="evh_7315092382398562_clickPreventStop($event, {})"
  >
  </ItpButton>
</template>
